import React, { useState, useEffect } from 'react';
import { ReturnArrowIcon, WisdomScrollIcon, CommunitySoulsIcon, SacredGearIcon, CreatePlusIcon, PreviewEyeIcon, ModifyPenIcon, RemoveXIcon, ConfirmCheckIcon, RemoveXIcon as CancelIcon, RegenerateCycleIcon } from './CustomIcons';

const AdminDashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('papers');
  const [papers, setPapers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [newPaperTopic, setNewPaperTopic] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPapers: 0,
    activeConnections: 0
  });

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch papers
      const papersRes = await fetch(`${API}/api/papers`);
      if (papersRes.ok) {
        const papersData = await papersRes.json();
        setPapers(papersData.papers || []);
      }

      // Fetch stats
      const statsRes = await fetch(`${API}/api/admin/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const handleGeneratePaper = async () => {
    if (!newPaperTopic.trim()) return;
    
    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/api/papers/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: newPaperTopic })
      });
      
      if (response.ok) {
        const newPaper = await response.json();
        setPapers(prev => [newPaper, ...prev]);
        setNewPaperTopic('');
      }
    } catch (error) {
      console.error('Failed to generate paper:', error);
    }
    setIsGenerating(false);
  };

  const handlePublishPaper = async (paperId) => {
    try {
      await fetch(`${API}/api/papers/${paperId}/publish`, { method: 'POST' });
      setPapers(prev => prev.map(p => 
        p.id === paperId ? { ...p, status: 'published' } : p
      ));
    } catch (error) {
      console.error('Failed to publish paper:', error);
    }
  };

  const handleDeletePaper = async (paperId) => {
    if (!window.confirm('Are you sure you want to delete this paper?')) return;
    
    try {
      await fetch(`${API}/api/papers/${paperId}`, { method: 'DELETE' });
      setPapers(prev => prev.filter(p => p.id !== paperId));
    } catch (error) {
      console.error('Failed to delete paper:', error);
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-300 font-mono">
      {/* Header */}
      <div className="border-b border-green-500/30 p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-green-400/70 hover:text-green-400 transition-colors flex items-center gap-2"
          >
            <ReturnArrowIcon size={16} color="#4ade80" />
            <span className="text-sm">[ RETURN ]</span>
          </button>
          <h1 className="text-green-400 text-xl">ADMIN DASHBOARD</h1>
        </div>
        <div className="text-green-500/50 text-sm">
          ARCHITECT ACCESS // LEVEL 3636
        </div>
      </div>

      {/* Stats Bar */}
      <div className="border-b border-green-500/20 p-4 grid grid-cols-3 gap-4">
        <div className="text-center">
          <div className="text-2xl text-green-400">{stats.totalUsers || 0}</div>
          <div className="text-green-500/50 text-xs">TOTAL USERS</div>
        </div>
        <div className="text-center">
          <div className="text-2xl text-green-400">{papers.length}</div>
          <div className="text-green-500/50 text-xs">WORKING PAPERS</div>
        </div>
        <div className="text-center">
          <div className="text-2xl text-green-400">{stats.activeConnections || 1}</div>
          <div className="text-green-500/50 text-xs">ACTIVE CONNECTIONS</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-green-500/20 flex">
        <button
          onClick={() => setActiveTab('papers')}
          className={`px-6 py-3 text-sm transition-colors flex items-center gap-2 ${
            activeTab === 'papers' 
              ? 'text-green-400 border-b-2 border-green-400' 
              : 'text-green-500/50 hover:text-green-400'
          }`}
        >
          <WisdomScrollIcon size={16} color={activeTab === 'papers' ? '#4ade80' : '#22c55e'} />
          WORKING PAPERS
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 text-sm transition-colors flex items-center gap-2 ${
            activeTab === 'users' 
              ? 'text-green-400 border-b-2 border-green-400' 
              : 'text-green-500/50 hover:text-green-400'
          }`}
        >
          <CommunitySoulsIcon size={16} color={activeTab === 'users' ? '#4ade80' : '#22c55e'} />
          USERS
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-6 py-3 text-sm transition-colors flex items-center gap-2 ${
            activeTab === 'settings' 
              ? 'text-green-400 border-b-2 border-green-400' 
              : 'text-green-500/50 hover:text-green-400'
          }`}
        >
          <SacredGearIcon size={16} color={activeTab === 'settings' ? '#4ade80' : '#22c55e'} />
          SETTINGS
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Papers Tab */}
        {activeTab === 'papers' && (
          <div className="space-y-6">
            {/* Generate New Paper */}
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <h3 className="text-green-400 mb-4 flex items-center gap-2">
                <CreatePlusIcon size={20} color="#4ade80" />
                GENERATE NEW WORKING PAPER
              </h3>
              <div className="flex gap-4">
                <input
                  type="text"
                  value={newPaperTopic}
                  onChange={(e) => setNewPaperTopic(e.target.value)}
                  placeholder="Enter topic or theme for today's paper..."
                  className="flex-1 bg-black/50 border border-green-500/30 rounded px-4 py-2 text-green-300 placeholder-green-500/30 outline-none focus:border-green-400"
                />
                <button
                  onClick={handleGeneratePaper}
                  disabled={isGenerating || !newPaperTopic.trim()}
                  className={`px-6 py-2 rounded flex items-center gap-2 transition-colors ${
                    isGenerating || !newPaperTopic.trim()
                      ? 'bg-green-900/30 text-green-500/30 cursor-not-allowed'
                      : 'bg-green-600/30 text-green-300 hover:bg-green-600/50 border border-green-500/50'
                  }`}
                >
                  {isGenerating ? (
                    <><RegenerateCycleIcon size={16} className="animate-spin" color="#22c55e" /> GENERATING...</>
                  ) : (
                    <><CreatePlusIcon size={16} color="#86efac" /> GENERATE</>
                  )}
                </button>
              </div>
            </div>

            {/* Papers List */}
            <div className="space-y-3">
              <h3 className="text-green-500/70 text-sm">EXISTING PAPERS</h3>
              
              {papers.length === 0 ? (
                <div className="text-green-500/40 text-center py-8">
                  No papers yet. Generate your first working paper above.
                </div>
              ) : (
                papers.map((paper) => (
                  <div 
                    key={paper.id}
                    className="bg-black/30 border border-green-500/20 rounded-lg p-4 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="text-green-300">{paper.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          paper.status === 'published' 
                            ? 'bg-green-600/30 text-green-400' 
                            : 'bg-yellow-600/30 text-yellow-400'
                        }`}>
                          {paper.status?.toUpperCase() || 'DRAFT'}
                        </span>
                      </div>
                      <p className="text-green-500/50 text-sm mt-1">{paper.date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-green-500/50 hover:text-green-400 transition-colors">
                        <PreviewEyeIcon size={16} color="#22c55e" />
                      </button>
                      <button className="p-2 text-green-500/50 hover:text-green-400 transition-colors">
                        <ModifyPenIcon size={16} color="#22c55e" />
                      </button>
                      {paper.status !== 'published' && (
                        <button 
                          onClick={() => handlePublishPaper(paper.id)}
                          className="p-2 text-green-500/50 hover:text-green-400 transition-colors"
                        >
                          <ConfirmCheckIcon size={16} color="#22c55e" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDeletePaper(paper.id)}
                        className="p-2 text-red-500/50 hover:text-red-400 transition-colors"
                      >
                        <RemoveXIcon size={16} color="#ef4444" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <h3 className="text-green-500/70 text-sm">COVENANT MEMBERS</h3>
            <div className="text-green-500/40 text-center py-8">
              User management coming soon.
              <br />
              <span className="text-xs">SSO integration with belongingsessions.com & elchme.com planned.</span>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-black/30 border border-green-500/20 rounded-lg p-4">
              <h3 className="text-green-400 mb-4">SYSTEM SETTINGS</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-green-300">Auto-Publish Papers</div>
                    <div className="text-green-500/50 text-xs">Automatically publish generated papers</div>
                  </div>
                  <button className="w-12 h-6 bg-green-900/30 rounded-full relative">
                    <div className="w-5 h-5 bg-green-500/50 rounded-full absolute left-0.5 top-0.5" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-green-300">Require Covenant Acceptance</div>
                    <div className="text-green-500/50 text-xs">Users must accept terms before access</div>
                  </div>
                  <button className="w-12 h-6 bg-green-600/30 rounded-full relative">
                    <div className="w-5 h-5 bg-green-400 rounded-full absolute right-0.5 top-0.5" />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-green-300">Heartbeat Audio</div>
                    <div className="text-green-500/50 text-xs">Enable sacred heartbeat throughout site</div>
                  </div>
                  <button className="w-12 h-6 bg-green-600/30 rounded-full relative">
                    <div className="w-5 h-5 bg-green-400 rounded-full absolute right-0.5 top-0.5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-black/30 border border-yellow-500/20 rounded-lg p-4">
              <h3 className="text-yellow-400 mb-4">SSO CONFIGURATION</h3>
              <div className="text-green-500/50 text-sm">
                SSO integration with belongingsessions.com and elchme.com is planned.
                <br />
                Shared database configuration will be available in a future update.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
